<template>
  <div class="color-management">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <h2>颜色管理</h2>
          <div class="header-actions">
            <el-button type="success" @click="handleDownloadTemplate">
              <el-icon><Download /></el-icon> 下载模板
            </el-button>
            <el-button type="warning" :disabled="!canEdit" @click="showImportDialog = true">
              <el-icon><Upload /></el-icon> 导入数据
            </el-button>
            <el-button type="danger" :disabled="!canEdit" @click="handleClearAll">
              <el-icon><Delete /></el-icon> 清空数据
            </el-button>
            <el-button type="warning" :disabled="!canEdit" @click="handleResetColorId">
              <el-icon><Refresh /></el-icon> 重置ID
            </el-button>
            <el-button type="primary" :disabled="!canEdit" @click="handleCreate">
              <el-icon><Plus /></el-icon> 新增颜色
            </el-button>
            <el-button @click="showColumnConfig = true">
              <el-icon><Setting /></el-icon> 列设置
            </el-button>
          </div>
        </div>
      </template>

      <!-- 搜索和筛选区域 -->
      <div class="search-section">
        <el-input
          v-model="searchKeyword"
          placeholder="输入手动代码、简码、英文名称或中文名称搜索..."
          @keyup.enter="loadData"
          clearable
          @clear="loadData"
        >
          <template #append>
            <el-button @click="loadData">
              <el-icon><Search /></el-icon>
            </el-button>
          </template>
        </el-input>

        <el-select
          v-model="filterActive"
          placeholder="状态筛选"
          @change="loadData"
          clearable
        >
          <el-option label="全部" value="" />
          <el-option label="启用" :value="true" />
          <el-option label="禁用" :value="false" />
        </el-select>

        <el-button @click="resetFilters">
          <el-icon><Refresh /></el-icon> 重置
        </el-button>
      </div>

      <!-- 数据表格 - 使用动态列渲染 -->
      <el-table
        ref="tableRef"
        :data="tableData"
        v-loading="loading"
        :default-sort="{ prop: 'manual_color_code', order: 'descending' }"
        style="width: 100%"
        border
        resizable
        @header-dragend="handleColumnResize"
        :key="tableKey"
      >
        <!-- 动态渲染列 -->
        <el-table-column 
          v-for="column in visibleColumns"
          :key="column.prop"
          :prop="column.prop"
          :label="column.label"
          :width="column.visible ? columnWidths[column.prop] : 0"
          :sortable="column.sortable"
          :resizable="column.resizable"
          :fixed="column.fixed"
          :show-overflow-tooltip="column.showOverflowTooltip"
        >
          <template #default="{ row }">
            <!-- 状态列特殊渲染 -->
            <template v-if="column.prop === 'is_active'">
              <el-tag :type="row.is_active ? 'success' : 'danger'">
                {{ row.is_active ? '启用' : '禁用' }}
              </el-tag>
            </template>
            
            <!-- 手动颜色代码列特殊渲染 -->
            <template v-else-if="column.prop === 'manual_color_code'">
              <div class="short-code-cell">
                <span>{{ row.manual_color_code }}</span>
                <el-tag v-if="row.is_manual_color_code" size="small" type="warning" class="manual-tag">
                  手动
                </el-tag>
              </div>
            </template>
            
            <!-- 简码列特殊渲染 -->
            <template v-else-if="column.prop === 'color_short_code'">
              <div class="short-code-cell">
                <span>{{ row.color_short_code }}</span>
                <el-tag v-if="row.is_manual_short_code" size="small" type="warning" class="manual-tag">
                  手动
                </el-tag>
              </div>
            </template>
            
            <!-- 日期列特殊渲染 -->
            <template v-else-if="column.prop === 'created_at' || column.prop === 'updated_at'">
              {{ formatDate(row[column.prop]) }}
            </template>
            
            <!-- 普通列渲染 -->
            <template v-else>
              {{ row[column.prop] }}
            </template>
          </template>
        </el-table-column>

        <!-- 操作列始终显示 -->
        <el-table-column 
          label="操作" 
          :width="columnWidths.operations" 
          fixed="right"
          resizable
        >
          <template #default="{ row }">
            <el-button size="small" :disabled="!canEdit" @click="handleEdit(row)">编辑</el-button>
            <el-button
              size="small"
              :type="row.is_active ? 'danger' : 'success'"
              :disabled="!canEdit"
              @click="handleToggleStatus(row)"
            >
              {{ row.is_active ? '禁用' : '启用' }}
            </el-button>
            <el-button size="small" type="danger" :disabled="!canEdit" @click="handleDelete(row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页控件 -->
      <div class="pagination-section">
        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadData"
          @current-change="loadData"
        />
      </div>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="showDialog"
      width="500px"
      @closed="resetForm"
    >
      <el-form
        :model="formData"
        :rules="formRules"
        ref="formRef"
        label-width="140px"
      >
        <el-form-item label="手动颜色代码" prop="manual_color_code">
          <AutoCodeInput
            v-model:modelValue="formData.manual_color_code"
            v-model:isManual="formData.is_manual_color_code"
            :auto-generate-tip="'自动生成：颜色ID的4位数字'"
            :manual-mode-tip="'手动编辑模式：修改后将不再自动更新'"
            :edit-button-text="'编辑代码'"
            :placeholder="'自动生成'"
            :maxlength="50"
            :cancel-confirm-message="'取消手动编辑后，手动颜色代码将恢复自动生成。确定要取消吗？'"
            @manual-enabled="onManualColorCodeEnabled"
            @manual-disabled="onManualColorCodeDisabled"
          />
        </el-form-item>
        
        <el-form-item label="颜色简码" prop="color_short_code">
          <AutoCodeInput
            v-model:modelValue="formData.color_short_code"
            v-model:isManual="formData.is_manual_short_code"
            :auto-generate-tip="'自动生成：与手动颜色代码相同（4位数字，如：0001、0002）'"
            :manual-mode-tip="'手动编辑模式：修改后将不再自动更新'"
            :edit-button-text="'编辑简码'"
            :placeholder="'自动生成'"
            :maxlength="10"
            :cancel-confirm-message="'取消手动编辑后，简码将恢复自动生成。确定要取消吗？'"
            @manual-enabled="onManualShortCodeEnabled"
            @manual-disabled="onManualShortCodeDisabled"
          />
        </el-form-item>
        
        <el-form-item label="内部英文名称" prop="internal_name_en">
          <el-input 
            v-model="formData.internal_name_en" 
            placeholder="请输入内部英文名称（自动转换为小写）" 
            maxlength="100" 
            show-word-limit 
          />
          <div class="field-tip">输入大写或小写字母，保存时自动转换为小写</div>
        </el-form-item>
        
        <el-form-item label="内部中文名称" prop="internal_name_cn">
          <el-input v-model="formData.internal_name_cn" placeholder="请输入内部中文名称" maxlength="100" show-word-limit />
        </el-form-item>
        
        <el-form-item label="状态" prop="is_active">
          <el-switch v-model="formData.is_active" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showDialog = false">取消</el-button>
          <el-button type="primary" :disabled="!canEdit" @click="() => submitForm()">确认</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 导入对话框 -->
    <el-dialog
      title="导入颜色数据"
      v-model="showImportDialog"
      width="600px"
      @closed="resetImportForm"
    >
      <el-alert
        type="info"
        :closable="false"
        style="margin-bottom: 20px;"
      >
        <template #title>
          <div>
            <p><strong>导入说明：</strong></p>
            <ul style="margin: 8px 0; padding-left: 20px;">
              <li>请先下载模板文件，按照模板格式填写数据</li>
              <li>列顺序：第一列-内部英文颜色（必填），第二列-手动颜色代码（可选），第三列-内部中文颜色（必填）</li>
              <li>手动颜色代码为可选：留空则系统自动生成（格式：4位数字如0001）</li>
              <li>颜色简码由系统自动生成（格式：与手动颜色代码相同，4位数字，如0001、0002）</li>
              <li>导入时会检查英文名称和手动颜色代码是否重复，重复的数据将被跳过</li>
              <li>导入后，手动颜色代码仍可在编辑界面手动修改或恢复为自动生成</li>
            </ul>
          </div>
        </template>
      </el-alert>

      <el-form
        ref="importFormRef"
        label-width="120px"
      >
        <el-form-item label="选择文件" required>
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :limit="1"
            accept=".xlsx,.xls"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :file-list="importFile ? [importFile] : []"
          >
            <template #trigger>
              <el-button type="primary" :disabled="!canEdit">选择Excel文件</el-button>
            </template>
            <template #tip>
              <div class="el-upload__tip">
                只能上传Excel文件（.xlsx或.xls），且不超过10MB
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showImportDialog = false">取消</el-button>
          <el-button
            type="primary"
            :loading="importLoading"
            :disabled="!importFile || !canEdit"
            @click="handleImport"
          >
            开始导入
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 列设置对话框 -->
    <ColumnConfigDialog
      :show="showColumnConfig"
      :column-config="columnConfig"
      :page-title="'颜色管理'"
      storage-key="color_management"
      :local-storage-key="colorColumnConfigFullStorageKey"
      @update:show="showColumnConfig = $event"
      @save="handleColumnSave"
      @reset-widths="handleColumnResetWidths"
      @reset-all="handleColumnResetAll"
      @select-all="handleColumnSelectAll"
      @unselect-all="handleColumnUnselectAll"
      @config-change="handleColumnConfigChange"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Search, Refresh, Setting, Download, Upload, Delete } from '@element-plus/icons-vue'
import ColumnConfigDialog from '@/components/ColumnConfigDialog.vue'
import AutoCodeInput from '@/components/AutoCodeInput/AutoCodeInput.vue'
import useColumnWidth from '@/composables/useColumnWidth'
import { getUserScopedStorageKey } from '@/utils/userScopedStorage'
import { useUserStore } from '@/store/user'
import { usePermissionKey } from '@/composables/usePermissionKey'

// API导入
import {
  getColors,
  createColor,
  updateColor,
  deleteColor,
  toggleColorStatus,
  downloadColorImportTemplate,
  importColors,
  clearAllColors,
  resetColorId
} from '@/api/colors'
import { buildColorCreatePayload, handleColorCreateError } from '@/utils/colorCreateFeedback'
import { confirmDangerousOperation } from '@/utils/dangerousOperationConfirm'

// 默认列配置
const defaultColumnConfig = [
  { prop: 'color_id', label: 'ID', visible: true, width: 80, sortable: true, resizable: true },
  { prop: 'manual_color_code', label: '手动颜色代码', visible: true, width: 140, sortable: true, resizable: true },
  { prop: 'color_short_code', label: '颜色简码', visible: true, width: 120, sortable: true, resizable: true },
  { prop: 'internal_name_en', label: '内部英文名称', visible: true, width: 180, sortable: true, resizable: true },
  { prop: 'internal_name_cn', label: '内部中文名称', visible: true, width: 180, sortable: true, resizable: true },
  { prop: 'is_active', label: '状态', visible: true, width: 100 },
  { prop: 'created_at', label: '创建时间', visible: true, width: 160, sortable: true },
  { prop: 'updated_at', label: '更新时间', visible: true, width: 160, sortable: true }
]

// 默认列宽配置
const defaultColumnWidths = {
  color_id: 80,
  manual_color_code: 140,
  color_short_code: 120,
  internal_name_en: 180,
  internal_name_cn: 180,
  is_active: 100,
  created_at: 160,
  updated_at: 160,
  operations: 200
}

// 列映射配置
const columnMap = {
  'ID': 'color_id',
  '手动颜色代码': 'manual_color_code',
  '颜色简码': 'color_short_code',
  '内部英文名称': 'internal_name_en',
  '内部中文名称': 'internal_name_cn',
  '状态': 'is_active',
  '创建时间': 'created_at',
  '更新时间': 'updated_at',
  '操作': 'operations'
}

// 使用列宽管理组合式函数
const { 
  tableRef, 
  columnWidths, 
  handleColumnResize, 
  resetColumnWidths,
  getColumnConfig 
} = useColumnWidth('color_management', defaultColumnWidths, columnMap)

const colorColumnConfigFullStorageKey = `${getUserScopedStorageKey('color_management')}_columns_config`

const userStore = useUserStore()
const canEdit = usePermissionKey('masterdata.colors.edit')

const loading = ref(false)
const searchKeyword = ref('')
const filterActive = ref('')
const showDialog = ref(false)
const showColumnConfig = ref(false)
const showImportDialog = ref(false)
const importLoading = ref(false)
const formRef = ref()
const importFormRef = ref()
const uploadRef = ref()
const isEditing = ref(false)
const tableData = ref([])
const columnConfig = ref([])
const tableKey = ref(0) // 用于强制重新渲染表格
const importFile = ref(null)

// 分页配置
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
})

// 表单数据
const formData = reactive({
  manual_color_code: '',
  is_manual_color_code: false,
  color_short_code: '',
  is_manual_short_code: false,
  internal_name_en: '',
  internal_name_cn: '',
  is_active: true
})

// 表单验证规则
const formRules = {
  internal_name_en: [
    { required: true, message: '请输入内部英文名称', trigger: 'blur' },
    { min: 1, max: 100, message: '长度在 1 到 100 个字符', trigger: 'blur' }
  ],
  internal_name_cn: [
    { required: true, message: '请输入内部中文名称', trigger: 'blur' },
    { min: 1, max: 100, message: '长度在 1 到 100 个字符', trigger: 'blur' }
  ]
}

// 计算属性
const dialogTitle = computed(() => 
  isEditing.value ? '编辑颜色' : '新增颜色'
)

// 可见列计算属性
const visibleColumns = computed(() => {
  return columnConfig.value.filter(col => col.visible)
})

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('zh-CN')
}

// 强制表格重新渲染
const forceTableRerender = () => {
  tableKey.value += 1
}

// 生命周期
onMounted(() => {
  userStore.initFromStorage()
  userStore.syncUserFromServer({ force: true })
  // 初始化列配置
  columnConfig.value = getColumnConfig(defaultColumnConfig)
  loadData()
})

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.currentPage,
      per_page: pagination.pageSize,
      search: searchKeyword.value,
      is_active: filterActive.value,
      order_by: 'manual_color_code',
      order: 'desc'
    }

    const response = await getColors(params)
    if (response.success) {
      tableData.value = response.data
      // 后端返回的是 pagination 对象
      if (response.pagination) {
        pagination.total = response.pagination.total || response.data.length
      } else {
        pagination.total = response.data.length
      }
    } else {
      ElMessage.error(response.message || '加载数据失败')
    }
  } catch (error) {
    ElMessage.error('网络错误，请稍后重试')
    console.error('加载颜色数据错误:', error)
  } finally {
    loading.value = false
  }
}

// 重置筛选条件
const resetFilters = () => {
  searchKeyword.value = ''
  filterActive.value = ''
  pagination.currentPage = 1
  loadData()
}

// 打开新增对话框
const handleCreate = () => {
  isEditing.value = false
  showDialog.value = true
}

// 打开编辑对话框
const handleEdit = (row) => {
  isEditing.value = true
  Object.assign(formData, row)
  showDialog.value = true
}

// 手动颜色代码事件处理
const onManualColorCodeEnabled = () => {
  console.log('手动颜色代码编辑已启用')
}

const onManualColorCodeDisabled = () => {
  console.log('手动颜色代码编辑已禁用')
}

// 颜色简码事件处理
const onManualShortCodeEnabled = () => {
  console.log('颜色简码编辑已启用')
}

const onManualShortCodeDisabled = () => {
  console.log('颜色简码编辑已禁用')
}

// 提交表单
const submitForm = async (forceCreate = false) => {
  // 如果传入的是事件对象（MouseEvent），则忽略它，使用默认值 false
  if (forceCreate && typeof forceCreate === 'object' && forceCreate.isTrusted !== undefined) {
    forceCreate = false
  }
  
  if (!formRef.value) return

  try {
    await formRef.value.validate()

    const submitData = buildColorCreatePayload(formData, forceCreate)
    
    console.log('========== 提交颜色数据 ==========')
    console.log('提交的数据:', submitData)
    console.log('英文名称:', submitData.internal_name_en)
    console.log('中文名称:', submitData.internal_name_cn)
    console.log('是否强制创建:', forceCreate)
    console.log('force_create 字段值:', submitData.force_create)
    console.log('================================')
    
    if (isEditing.value) {
      // 编辑现有颜色
      await updateColor(formData.color_id, submitData)
      ElMessage.success('颜色更新成功')
      showDialog.value = false
      loadData()
    } else {
      // 创建新颜色
      try {
        await createColor(submitData)
        ElMessage.success('颜色创建成功')
        showDialog.value = false
        loadData()
      } catch (error) {
        await handleColorCreateError(error, {
          onConfirmForceCreate: () => submitForm(true)
        })
      }
    }
  } catch (error) {
    if (error === 'cancel') {
      // 用户取消操作，不做任何处理
      ElMessage.info('已取消创建')
    } else if (error.errors) {
      console.error('表单验证错误:', error.errors)
      ElMessage.warning('请检查表单填写是否正确')
    } else {
      console.error('提交表单错误:', error)
      ElMessage.error(error.response?.data?.message || '操作失败，请稍后重试')
    }
  }
}

// 删除颜色
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除颜色「${row.internal_name_en}」吗？此操作不可恢复。`,
      '警告',
      { 
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      }
    )
    
    await deleteColor(row.color_id)
    ElMessage.success('删除成功')
    loadData()
  } catch (error) {
    if (error === 'cancel') {
      ElMessage.info('已取消删除')
    } else {
      ElMessage.error('删除失败')
      console.error('删除颜色错误:', error)
    }
  }
}

// 切换状态
const handleToggleStatus = async (row) => {
  try {
    await toggleColorStatus(row.color_id, { is_active: !row.is_active })
    ElMessage.success('状态更新成功')
    loadData()
  } catch (error) {
    ElMessage.error('状态更新失败')
    console.error('切换状态错误:', error)
  }
}

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields()
  }
  Object.assign(formData, {
    manual_color_code: '',
    is_manual_color_code: false,
    color_short_code: '',
    is_manual_short_code: false,
    internal_name_en: '',
    internal_name_cn: '',
    is_active: true
  })
  isEditing.value = false
}

// 列设置相关方法
const handleColumnSave = (newConfig) => {
  columnConfig.value = newConfig
  // 更新列宽配置
  newConfig.forEach(col => {
    if (columnWidths[col.prop] !== undefined) {
      columnWidths[col.prop] = col.width
    }
  })
  // 强制表格重新渲染以应用新的列配置
  forceTableRerender()
  ElMessage.success('列设置已保存')
}

const handleColumnResetWidths = () => {
  resetColumnWidths()
  // 同时重置配置中的宽度
  columnConfig.value.forEach(col => {
    const defaultCol = defaultColumnConfig.find(c => c.prop === col.prop)
    if (defaultCol) {
      col.width = defaultCol.width
    }
  })
  forceTableRerender()
}

const handleColumnResetAll = () => {
  resetColumnWidths()
  columnConfig.value = [...defaultColumnConfig]
  forceTableRerender()
}

const handleColumnSelectAll = () => {
  columnConfig.value.forEach(col => {
    col.visible = true
  })
  forceTableRerender()
}

const handleColumnUnselectAll = () => {
  columnConfig.value.forEach(col => {
    if (col.prop !== 'operations') {
      col.visible = false
    }
  })
  forceTableRerender()
}

const handleColumnConfigChange = (newConfig) => {
  columnConfig.value = newConfig
  // 实时应用列配置变化
  forceTableRerender()
}

// 下载模板
const handleDownloadTemplate = async () => {
  try {
    const blob = await downloadColorImportTemplate()
    
    // 验证 blob 大小，确保文件不为空
    if (!blob || blob.size === 0) {
      ElMessage.error('下载的文件为空，请稍后重试')
      return
    }
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '颜色导入模板.xlsx'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('模板下载成功')
  } catch (error) {
    console.error('下载模板失败:', error)
    ElMessage.error(error.response?.data?.message || '模板下载失败，请稍后重试')
  }
}

// 文件选择变化
const handleFileChange = (file) => {
  importFile.value = file.raw
}

// 移除文件
const handleFileRemove = () => {
  importFile.value = null
}

// 重置导入表单
const resetImportForm = () => {
  if (importFormRef.value) {
    importFormRef.value.resetFields()
  }
  importFile.value = null
}

// 导入数据
const handleImport = async () => {
  if (!importFile.value) {
    ElMessage.warning('请选择要导入的文件')
    return
  }

  try {
    await ElMessageBox.confirm(
      '导入数据将根据英文名称或手动颜色代码进行匹配，已存在的数据将被跳过。确定要导入吗？',
      '确认导入',
      {
        type: 'warning',
        confirmButtonText: '确定导入',
        cancelButtonText: '取消'
      }
    )

    importLoading.value = true
    const response = await importColors(importFile.value)
    
    // 响应拦截器已经返回了 response.data，所以直接使用 response
    if (response.success) {
      const { created_count, errors } = response
      let message = `成功导入 ${created_count} 条颜色数据`
      
      if (errors && errors.length > 0) {
        message += `，但有 ${errors.length} 条数据导入失败`
        // 显示所有错误，使用 ElMessageBox 显示详细信息
        console.warn('导入错误详情:', errors)
        ElMessage.warning({
          message: `${message}。请查看浏览器控制台查看详细错误信息（共 ${errors.length} 条）`,
          duration: 8000,
          showClose: true
        })
        
        // 显示所有错误详情
        const errorText = errors.map((err, idx) => `${idx + 1}. ${err}`).join('\n')
        console.log(`导入错误详情（共 ${errors.length} 条）：\n${errorText}`)
      } else {
        ElMessage.success(message)
      }
      
      showImportDialog.value = false
      // 导入后重置到第一页并重新加载数据
      pagination.currentPage = 1
      loadData()
    } else {
      const errors = response.errors || []
      if (errors.length > 0) {
        ElMessage.error({
          message: `导入失败：${errors.slice(0, 3).join('；')}${errors.length > 3 ? '...' : ''}`,
          duration: 5000
        })
      } else {
        ElMessage.error(response.message || '导入失败')
      }
    }
  } catch (error) {
    if (error === 'cancel') {
      ElMessage.info('已取消导入')
    } else {
      console.error('导入失败:', error)
      // 检查错误响应
      const errorData = error.response?.data || error.data || {}
      const errorMessage = errorData.message || '导入失败，请稍后重试'
      const errors = errorData.errors || []
      
      if (errors.length > 0) {
        // 显示错误详情
        const errorText = errors.slice(0, 5).map((err, idx) => `${idx + 1}. ${err}`).join('\n')
        const moreErrors = errors.length > 5 ? `\n...还有 ${errors.length - 5} 条错误` : ''
        
        ElMessageBox.alert(
          `导入失败，发现以下错误：\n\n${errorText}${moreErrors}`,
          '导入错误',
          {
            type: 'error',
            confirmButtonText: '确定',
            dangerouslyUseHTMLString: false
          }
        )
      } else {
        ElMessage.error(errorMessage)
      }
    }
  } finally {
    importLoading.value = false
  }
}

// 清空所有数据
const handleClearAll = async () => {
  try {
    const operationPassword = await confirmDangerousOperation({
      message:
      `<div style="text-align: left;">
        <p><strong>警告：此操作将清空所有颜色数据！</strong></p>
        <p style="margin-top: 10px;">操作说明：</p>
        <ul style="margin: 10px 0; padding-left: 20px;">
          <li>清空所有颜色数据，包括颜色详情</li>
          <li>所有关联表的颜色引用将被设置为空（NULL）</li>
          <li>材料身份、订单项等需要手动重新选择颜色</li>
          <li>此操作不可恢复！</li>
        </ul>
        <p style="color: #E6A23C; margin-top: 10px;"><strong>确定要继续吗？</strong></p>
      </div>`,
      title: '警告',
      confirmButtonText: '确定清空',
      html: true,
    })

    loading.value = true
    const response = await clearAllColors(operationPassword)
    
    if (response?.success) {
      const deleted = response.deleted_count ?? response.data?.deleted_count ?? 0
      ElMessage({
        message: `成功清空 ${deleted} 条颜色数据。请注意：材料身份、订单项等需要手动重新选择颜色。`,
        type: 'success',
        duration: 8000,
        showClose: true
      })
      loadData()
    } else {
      ElMessage.error(response?.message || '清空失败')
    }
  } catch (error) {
    if (error === 'cancel') {
      ElMessage.info('已取消清空操作')
    } else {
      console.error('清空数据失败:', error)
      ElMessage.error(error.response?.data?.message || '清空失败，请稍后重试')
    }
  } finally {
    loading.value = false
  }
}

// 重置color ID
const handleResetColorId = async () => {
  try {
    const operationPassword = await confirmDangerousOperation({
      message:
      `<div style="text-align: left;">
        <p><strong>警告：此操作将重置color ID到初始状态！</strong></p>
        <p style="margin-top: 10px;">操作说明：</p>
        <ul style="margin: 10px 0; padding-left: 20px;">
          <li>将删除所有颜色数据</li>
          <li>将color ID的AUTO_INCREMENT重置为1（从1开始）</li>
          <li>所有关联表的颜色引用将被设置为空（NULL）</li>
          <li>材料身份、订单项等需要手动重新选择颜色</li>
          <li>此操作不可恢复！</li>
        </ul>
        <p style="color: #E6A23C; margin-top: 10px;"><strong>确定要继续吗？</strong></p>
      </div>`,
      title: '警告',
      confirmButtonText: '确定重置',
      html: true,
    })

    loading.value = true
    const response = await resetColorId(operationPassword)
    
    if (response.success) {
      ElMessage({
        message: `成功重置color ID，已删除 ${response.deleted_count} 条颜色数据。现在可以重新导入数据，ID将从1开始。`,
        type: 'success',
        duration: 8000,
        showClose: true
      })
      loadData()
    } else {
      ElMessage.error(response.message || '重置失败')
    }
  } catch (error) {
    if (error === 'cancel') {
      ElMessage.info('已取消重置操作')
    } else {
      console.error('重置color ID失败:', error)
      ElMessage.error(error.response?.data?.message || '重置失败，请稍后重试')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.color-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.search-section {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.short-code-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.manual-tag {
  font-size: 10px;
  padding: 0 4px;
  height: 18px;
  line-height: 18px;
}

.field-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

/* 确保表头文本不换行 */
:deep(.el-table .el-table__header-wrapper .el-table__header th .cell) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>